import { Box } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import { StickyActionBar } from '../../../core-components/theme/components/StickyActionBar';
import { useFocusOnMount } from '../../../utilities/customHooks/useFocusOnMount';
import type { AlertChannel, PasscodeChannel } from '../types/domain.types';

import { AuthPanelHeading, AuthSubtitle, authContentSx, authPanelSx } from './AuthPanel';
import { IdentifierField } from './IdentifierField';
import type { IdentifierFieldProps } from './IdentifierField';
import { OtherChannelSendButton, PrimarySendButton } from './SendCodeButtons';
import type { SendState } from './SendCodeButtons';

export type IdentifierFieldState = Pick<IdentifierFieldProps, 'value' | 'kind' | 'helperText' | 'error' | 'onChange'>;

export interface IdentifyPanelProps {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly field: IdentifierFieldState;
  readonly fieldProblemKey: number;
  readonly codeChoices: readonly AlertChannel[];
  readonly sendingChannel: PasscodeChannel | null;
  readonly sending: boolean;
  readonly checkingPassword: boolean;
  readonly retrying: boolean;
  readonly held: boolean;
  readonly notice?: React.ReactNode;
  readonly onSend: (channel?: AlertChannel) => void;
  readonly onRetry: () => void;
  readonly onUsePassword: () => void;
}

interface PrimaryActionProps {
  readonly retrying: boolean;
  readonly sending: boolean;
  readonly channel: AlertChannel | undefined;
  readonly sendState: SendState;
  readonly onRetry: () => void;
}

const PrimaryAction: React.FC<PrimaryActionProps> = ({ retrying, sending, channel, sendState, onRetry }) => {
  const { t } = useTranslation();
  if (!retrying) return <PrimarySendButton channel={channel} {...sendState} />;
  return (
    <Button type="button" intent="primary" busy={sending} onClick={onRetry}>
      {t('passcodeAuth.login.retry', 'Retry')}
    </Button>
  );
};

export const IdentifyPanel: React.FC<IdentifyPanelProps> = ({
  title,
  subtitle,
  field,
  fieldProblemKey,
  codeChoices,
  sendingChannel,
  sending,
  checkingPassword,
  retrying,
  held,
  notice,
  onSend,
  onRetry,
  onUsePassword,
}) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  const noticeId = useId();
  const inputRef = useFocusOnMount<HTMLInputElement>(fieldProblemKey > 0, fieldProblemKey);
  const hasSubtitle = subtitle !== undefined && subtitle !== null;
  const [firstChoice, otherChoice] = codeChoices;
  const sendState: SendState = { sendingChannel, sending, unavailable: checkingPassword || held, describedBy: held ? noticeId : undefined };
  const sendFirst = (): void => onSend(firstChoice);
  const usePassword = (
    <Button
      type="button"
      intent={otherChoice === undefined ? 'secondary' : 'tertiary'}
      busy={checkingPassword}
      aria-disabled={sending || undefined}
      onClick={onUsePassword}
    >
      {t('passcodeAuth.login.usePassword', 'Use password')}
    </Button>
  );
  const otherChannel = otherChoice === undefined ? undefined : <OtherChannelSendButton channel={otherChoice} onSend={onSend} {...sendState} />;

  return (
    <Box
      component="form"
      noValidate
      sx={authPanelSx}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        sendFirst();
      }}
    >
      <Box sx={authContentSx}>
        <AuthPanelHeading title={title}>{hasSubtitle && <AuthSubtitle id={subtitleId}>{subtitle}</AuthSubtitle>}</AuthPanelHeading>
        <IdentifierField
          {...field}
          inputRef={inputRef}
          describedById={hasSubtitle ? subtitleId : undefined}
          autoFocus
          onEnter={retrying ? onRetry : sendFirst}
        />
        <Box id={noticeId}>{notice}</Box>
      </Box>
      <StickyActionBar
        layout="dock"
        announce={false}
        tertiary={otherChannel === undefined ? undefined : usePassword}
        secondary={otherChannel ?? usePassword}
        primary={<PrimaryAction retrying={retrying} sending={sending} channel={firstChoice} sendState={sendState} onRetry={onRetry} />}
      />
    </Box>
  );
};
